import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { deptLabel } from "../../lib/labels";
import { toast } from "sonner";
import { Plus, X, RefreshCw, Trash2 } from "lucide-react";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import EmptyState from "../../components/system/EmptyState";
import Input, { Select } from "../../components/system/Input";
import { TENANT_CATALOG_ONBOARDING_ENABLED } from "../../lib/tenantCatalogAvailability";

const PAGE_CAP = 1000;
const ALL_DEPTS = "__all__";
const STORAGE_KEY = "shelflife.admin.products.dept_filter";

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [deptCount, setDeptCount] = useState(null);
  const [catalogTotal, setCatalogTotal] = useState(null);
  const [depts, setDepts] = useState([]);
  const [deptFilter, setDeptFilter] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [q, setQ] = useState("");
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ barcode: "", name: "", category: "", department_id: "" });
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/departments");
        setDepts(d);
        if (!deptFilter) {
          const cheese = d.find((x) => x.name.toLowerCase() === "cheese");
          const fallback = cheese?.id || d[0]?.id || "";
          setDeptFilter(fallback);
        }
        if (!form.department_id && d[0]) setForm((f) => ({ ...f, department_id: d[0].id }));
      } catch (e) { toast.error(formatApiErrorDetail(e)); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (deptFilter) localStorage.setItem(STORAGE_KEY, deptFilter); }, [deptFilter]);

  const load = async () => {
    try {
      const productParams = { limit: PAGE_CAP };
      const countParams = {};
      if (q) productParams.q = q;
      if (deptFilter && deptFilter !== ALL_DEPTS) {
        productParams.department_id = deptFilter;
        countParams.department_id = deptFilter;
      }
      const [p, c, s] = await Promise.all([
        api.get("/products", { params: productParams }),
        api.get("/products/count", { params: countParams }),
        api.get("/admin/stats").catch(() => ({ data: null })),
      ]);
      setProducts(p.data);
      setDeptCount(c.data?.count ?? null);
      if (s.data?.products != null) setCatalogTotal(s.data.products);
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (deptFilter || deptFilter === ALL_DEPTS) load(); }, [q, deptFilter]);

  const create = async () => {
    setSubmitting(true);
    try {
      await api.post("/products", form);
      toast.success("Product added");
      setShow(false);
      setForm({ barcode: "", name: "", category: "", department_id: depts[0]?.id || "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); } finally { setSubmitting(false); }
  };

  const refreshCatalog = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/refresh-catalog");
      toast.success(`Catalog refreshed — ${data.count} products`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setRefreshing(false); }
  };

  const deactivate = async (p) => {
    if (!window.confirm("Deactivate this product? Existing inventory history will be preserved.")) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deactivated");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const currentDept = depts.find((d) => d.id === deptFilter);
  const headerScope = deptFilter === ALL_DEPTS ? "ALL DEPARTMENTS" : currentDept ? deptLabel(currentDept.name) : "—";
  const headerCount = deptFilter === ALL_DEPTS ? catalogTotal : deptCount;

  return (
    <div data-testid="admin-products-page">
      <PageHeader
        eyebrow="Master catalog"
        title="Products"
        subtitle={
          <span data-testid="products-count-summary" className="inline-flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-ink">{headerScope}</span>
            <span className="text-ink-3">·</span>
            <span>
              <span className="font-mono text-ink">{(headerCount ?? 0).toLocaleString()}</span>{" "}
              product{headerCount === 1 ? "" : "s"}
            </span>
            {products.length < (headerCount ?? 0) && (
              <>
                <span className="text-ink-3">·</span>
                <span>showing <span className="font-mono text-ink">{products.length.toLocaleString()}</span></span>
              </>
            )}
            {!q && products.length >= PAGE_CAP && (
              <span className="text-status-urgent">· refine with search to see more</span>
            )}
          </span>
        }
        actions={
          <Button tier="primary" onClick={() => setShow(true)} data-testid="new-product-button">
            <Plus size={16} /> New product
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Select
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          className="min-w-[180px]"
          data-testid="products-dept-filter"
        >
          <option value={ALL_DEPTS}>All departments</option>
          {depts.map((d) => <option key={d.id} value={d.id}>{deptLabel(d.name)}</option>)}
        </Select>
        <Input
          variant="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${headerScope === "ALL DEPARTMENTS" ? "catalog" : headerScope}…`}
          className="flex-1 min-w-[240px] max-w-md"
          data-testid="products-search-input"
        />
        {TENANT_CATALOG_ONBOARDING_ENABLED && (
          <Button
            tier="secondary"
            onClick={refreshCatalog}
            disabled={refreshing}
            data-testid="refresh-catalog-button"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing…" : "Refresh from Sheet"}
          </Button>
        )}
      </div>

      {products.length === 0 ? (
        <EmptyState
          title={q ? `No matches in ${headerScope}` : `No products in ${headerScope}`}
          description={q ? "Try a different search term or department." : "No products are available in this department."}
          data-testid="products-empty"
        />
      ) : (
        <Card variant="raised" className="overflow-hidden">
          {products.map((p, idx) => {
            const dept = depts.find((d) => d.id === p.department_id);
            return (
              <div
                key={p.id}
                className={`flex items-center gap-3 p-4 ${idx > 0 ? "border-t border-border/40" : ""}`}
                data-testid={`product-admin-row-${p.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold truncate">{p.name}</div>
                  <div className="data-sku truncate">{p.barcode} · {p.category || "—"}</div>
                </div>
                <div className="flex items-center gap-3 ml-3 shrink-0">
                  <div className="font-mono text-xs text-ink-2">{dept ? deptLabel(dept.name) : "—"}</div>
                  <Button
                    tier="secondary"
                    size="sm"
                    onClick={() => deactivate(p)}
                    className="text-status-urgent"
                    data-testid={`deactivate-product-${p.id}`}
                    title="Deactivate product"
                  >
                    <Trash2 size={12} /> Deactivate
                  </Button>
                </div>
              </div>
            );
          })}
        </Card>
      )}

      {show && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 px-3">
          <Card variant="raised" className="w-full sm:max-w-md p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="font-display font-black text-2xl">New product</div>
              <button onClick={() => setShow(false)} className="p-2 -mt-2 -mr-2 rounded-button text-ink-2 hover:text-ink">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-3">
              <Input placeholder="Barcode" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} className="font-mono" data-testid="new-product-barcode" />
              <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-product-name" />
              <Input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="new-product-category" />
              <Select value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} data-testid="new-product-department">
                {depts.map((d) => <option key={d.id} value={d.id}>{deptLabel(d.name)}</option>)}
              </Select>
              <Button tier="primary" className="w-full" onClick={create} disabled={submitting} data-testid="submit-new-product">
                {submitting ? "Adding…" : "Add product"}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
